<template>
  <div class="container-fluid pt-0 mt-2">
    <!-- Loading Indicator -->
    <loading
      :active.sync="isLoading"
      :is-full-page="fullPage"
      background-color="#f3f3f3"
      :opacity="0.8"
      loader="bars"
    ></loading>

    <!-- Top breadcrumb / session title -->

    <div class="row card_row mb-3">
      <div class="col-md-3 col-sm-4">
        <IconCard
          :label="$t('sessions')"
          :title="sessionsCount || 0"
          :iconColor="'background-color: rgba(158, 198, 221, 0.30);'"
        >
          <template #icon>
            <IconDevices />
          </template>
        </IconCard>
      </div>
    </div>

    <!-- Table start -->
    <div
      style="
        background-color: #fff;
        max-height: 40rem;
        overflow-y: auto;
        width: 100%;
        height: fit-content;
        margin-top: 2rem;
      "
    >
      <div class="card-body" style="overflow-x: auto; width: 100%">
        <!-- dropdown + search row -->
        <div class="table-responsive">
          <div class="row mb-2">
            <div class="col-sm-12 col-md-12">
              <div
                class="d-flex align-items-center justify-content-between"
                style="gap: 0.4rem"
              >
                <!-- Status dropdown -->
                <div
                  style="
                    width: 14rem;
                    background-color: #fff;
                    border-radius: 8px;
                    border: 1px solid rgba(183, 195, 205, 1);
                  "
                >
                  <select
                    class="form-control"
                    v-model="statusFilter"
                    @change="sortDevices"
                    style="
                      width: 13rem;
                      background-color: transparent !important;
                      cursor: pointer;
                    "
                  >
                    <option value="all">{{ $t("all_devices") }}</option>
                    <option value="active">{{ $t("active") }}</option>
                    <option value="inactive">{{ $t("inactive") }}</option>
                    <option value="completed">{{ $t("completed") }}</option>
                  </select>
                </div>

                <!-- Search -->
                <TableSearch @handleSearch="handleSearch" />
              </div>
            </div>
          </div>
        </div>

        <!-- table + pagination row -->
        <div class="row">
          <div class="col-md-12">
            <div ref="content" class="table-responsive">
              <table id="session-listing" class="table table-hover display">
                <thead style="background-color: rgba(240, 244, 251, 0.6)">
                  <tr>
                    <th v-for="col in columns" :key="col[1]">
                      {{ col[0] }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(session, idx) in paginatedData"
                    :key="session.id"
                    :class="{ 'data-table-bg': idx % 2 !== 0 }"
                    @click="viewSession(session)"
                    class="cursor-pointer"
                  >
                    <td>{{ session.name || "N/A" }}</td>
                    <td>{{ formatDate(session.startDate) }}</td>
                    <td>{{ formatDate(session.endDate) }}</td>
                    <td>
                      <div
                        class="d-flex align-items-center position-relative"
                        v-click-outside="hideSessionImeis"
                        v-if="session.devices && session.devices.length > 0"
                      >
                        {{ session.devices[0].imei || "N/A" }}
                        <button
                          v-if="session.devices.length > 1"
                          class="d-flex justify-content-center align-items-center border-0 ml-2 outline-none font-weight-bold"
                          style="
                            width: 31px;
                            height: 31px;
                            border-radius: 50%;
                            background-color: #e9ecf2;
                          "
                          @click.stop="showSessionImeis($event, session.id)"
                        >
                          <span class="text-primary font-12">
                            +{{ session.devices.length - 1 }}
                          </span>
                        </button>

                        <div
                          class="dropdown-menu-container border-0 shadow"
                          style="top: 100%; left: 0; border-radius: 10px"
                          v-show="
                            activeSessionId === session.id && showImeiDropdown
                          "
                        >
                          <p
                            v-for="(device, index) in session.devices"
                            :key="index"
                            class="mb-0 px-3 py-2"
                          >
                            {{ device.imei || "N/A" }}
                          </p>
                        </div>
                      </div>
                      <span v-else>No devices</span>
                    </td>
                    <td>
                      <div
                        class="d-flex align-items-center text-capitalize"
                        style="column-gap: 0.3rem"
                      >
                        <div
                          v-if="
                            !session.status || session.status === 'inactive'
                          "
                          style="
                            height: 0.8rem;
                            width: 0.8rem;
                            border-radius: 0.4rem;
                          "
                          class="bg-secondary"
                        ></div>
                        <div
                          v-else-if="['completed'].includes(session.status)"
                          style="
                            height: 0.8rem;
                            width: 0.8rem;
                            border-radius: 0.4rem;
                          "
                          class="bg-success"
                        ></div>
                        <div
                          v-else
                          style="
                            height: 0.8rem;
                            width: 0.8rem;
                            border-radius: 0.4rem;
                          "
                          class="bg-danger"
                        ></div>
                        <span>{{ session.status }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!sessions.data || sessions.data.length === 0">
                    <td :colspan="columns.length" class="text-center py-4">
                      {{ $t("no_data_found") }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <Pagination
              :pageCount="paginationMeta.totalPages || 1"
              :data="sessions.data"
              :sizeperpage="sizeperpage"
              :pageNumber="pageNumber"
              :pages="pages"
              rowOption="table"
              @goToLastPage="goToLastPage"
              @nextPage="nextPage"
              @handlePageChange="handlePageChange"
              @goToFirstPage="goToFirstPage"
              @prevPage="prevPage"
              @onchange="onchange"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- table end -->
  </div>
</template>

<script>
import TableSearch from "../../components/TableSearch.vue";
import Pagination from "../../components/PaginationNew.vue";
import "vue-loading-overlay/dist/vue-loading.css";
import IconCard from "../../components/IconCard.vue";
import IconDevices from "../../components/Iconsvg/IconDevices.vue";
import Loading from "vue-loading-overlay";
import { mapGetters, mapActions } from "vuex";
import tableMixin from "../../mixins/tableMixinWithBackend";
import vClickOutside from "v-click-outside";

export default {
  name: "DeviceSessions",
  directives: { clickOutside: vClickOutside.directive },
  components: {
    TableSearch,
    Pagination,
    IconCard,
    IconDevices,
    Loading,
  },
  mixins: [tableMixin],
  data() {
    return {
      isLoading: false,
      fullPage: true,
      statusFilter: "all",
      searchQuery: "",
      activeSessionId: null,
      showImeiDropdown: false,
      columns: [
        ["Session Name", "name"],
        ["Start Date", "startDate"],
        ["End Date", "endDate"],
        ["Devices", "devices"],
        ["Status", "status"],
      ],
      selectedRows: [],
      dataTable: [],
    };
  },
  computed: {
    ...mapGetters(["canPerformAction", "sessionsCount", "sessions"]),
    paginationMeta() {
      return this.sessions.meta || {};
    },
    tableColumns() {
      return this.columns.filter((col) => col[1] !== "action");
    },
  },
  async created() {
    this.isLoading = true;
    try {
      await Promise.all([
        this.fetchSessionsCount(),
        this.fetchSessionsWithLoader(this.params),
      ]);
    } catch (error) {
      console.error("Error in created hook:", error);
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions([
      "fetchSessionsCount",
      "fetchSessions",
      "setSelectedSession",
    ]),

    async fetchSessionsWithLoader(params) {
      this.isLoading = true;
      try {
        await this.fetchSessions(params);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    getStatusBadgeClass(session) {
      const now = new Date();
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);

      if (now < startDate) {
        return "bg-warning text-dark";
      } else if (now >= startDate && now <= endDate) {
        return "bg-success text-white";
      } else {
        return "bg-light text-dark";
      }
    },
    getStatusText(session) {
      const now = new Date();
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);

      if (now < startDate) {
        return "Scheduled";
      } else if (now >= startDate && now <= endDate) {
        return "Active";
      } else {
        return "Completed";
      }
    },
    viewSession(session) {
      if (session && session.id) {
        // Store the selected session in Vuex
        this.setSelectedSession(session);

        // Navigate to the session history page
        this.$router.push({
          name: "DeviceSessionHistory",
          params: { id: session.id },
        });
      }
    },
    async doSearch() {
      const params = {
        ...this.params,
        ...(this.searchQuery && { keyword: this.searchQuery }),
        ...(this.statusFilter !== "all" && { status: this.statusFilter }),
      };
      await this.fetchSessionsWithLoader(params);
    },

    async sortDevices() {
      const params = {
        ...this.params,
        ...(this.searchQuery && { keyword: this.searchQuery }),
        ...(this.statusFilter !== "all" && { status: this.statusFilter }),
      };
      await this.fetchSessionsWithLoader(params);
    },

    showSessionImeis(event, sessionId) {
      event.stopPropagation();
      this.activeSessionId = sessionId;
      this.showImeiDropdown = true;
    },

    hideSessionImeis() {
      this.showImeiDropdown = false;
    },
    exportSessionsToCsv() {
      if (!this.sessions?.data?.length) return;

      const headers = [
        "Session Name",
        "Start Date",
        "End Date",
        "Devices",
        "Status",
      ];

      const csvData = this.paginatedData.map((session) => ({
        name: `"${session.name || "N/A"}"`,
        startDate: `"${this.formatDate(session.startDate) || "N/A"}"`,
        endDate: `"${this.formatDate(session.endDate) || "N/A"}"`,
        devices: `"${
          session.devices
            ?.map((d) => d.imei)
            .filter(Boolean)
            .join(", ") || "N/A"
        }"`,
        status: `"${this.getStatusText(session) || "N/A"}"`,
      }));

      // Create CSV content
      let csvContent = [
        headers.join(","),
        ...csvData.map((item) => Object.values(item).join(",")),
      ].join("\n");

      // Create download link
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `sessions_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
  watch: {
    searchQuery() {
      this.debouncedSearch();
    },
    sizeperpage() {
      this.pageNumber = 1;
      this.sortDevices();
    },
    pageNumber() {
      this.sortDevices();
    },
    sessions() {
      this.dataTable = this.sessions.data;
    },
  },
  mounted() {
    this.$root.$on("trigger-export-sessions-csv", this.exportSessionsToCsv);
  },
  beforeDestroy() {
    this.$root.$off("trigger-export-sessions-csv", this.exportSessionsToCsv);
  },
};
</script>

<style scoped>
.data-table-bg {
  background-color: rgba(240, 244, 251, 0.35);
}
.card_row {
  display: flex;
  gap: 1.5rem;
  padding: 0 1rem 0;
}
.dropdown-menu-container {
  top: 100%;
  left: 0px;
  border-radius: 10px;
  background: white;
  z-index: 1;
  position: absolute;
}
</style>
